<script setup lang="ts">
/**
 * Preferences (Ctrl+,).
 *
 * Every control writes through `patchSettings` immediately rather than
 * collecting changes behind an OK button. Settings live in main and are
 * broadcast to every window, so a deferred apply would mean holding a second,
 * divergent copy of them here and reconciling it — and a preference that takes
 * effect as you change it is easier to understand than one that does not.
 *
 * The dialog is a view over the settings store, so anything changed elsewhere
 * — the Themes menu, a View toggle — is reflected here without wiring.
 */
import IconClose from './IconClose.vue'
import { computed, nextTick, onBeforeUnmount, onUpdated, ref, watch } from 'vue'
import { filterSettings } from '../utils/settings-filter'
import { patchSettings, setContentWidth, setFontSize, useSettingsStore } from '../stores/settings'
import { useThemeStore } from '../stores/theme'
import { chooseTheme } from '../stores/system-theme'
import { setPunctuation } from '../editor/punctuation'
import { setShowWhitespace } from '../editor/whitespace'
import { setEditorModes } from '../editor/typewriter'
import { invalidateCommands } from '../commands/registry'
import { refreshDecorations } from '../editor/view'
import { useFocusTrap } from '../composables/useFocusTrap'
import { AUTOSAVE_MAX_MS, AUTOSAVE_MIN_MS, autoSaveDelay } from '../commands/autosave'
import {
  FONT_MAX,
  FONT_MIN,
  LINE_HEIGHT_MAX,
  LINE_HEIGHT_MIN,
  WIDTH_MAX,
  WIDTH_MIN,
  effectiveFontSize,
  validFontFamily,
  validLineHeight,
  validWidth,
} from '../stores/appearance'

const props = defineProps<{ open: boolean }>()
const emit = defineEmits<{ close: [] }>()

const settings = useSettingsStore()
const themes = useThemeStore()
const panel = ref<HTMLElement | null>(null)
useFocusTrap(panel, () => props.open)

/** What the settings shown are narrowed to; see utils/settings-filter.ts. */
const filter = ref('')
const matches = ref(0)
const body = ref<HTMLElement | null>(null)
const filterInput = ref<HTMLInputElement | null>(null)

function applyFilter(): void {
  if (body.value) matches.value = filterSettings(body.value, filter.value)
}
watch(filter, applyFilter, { flush: 'post' })
// Rows that come and go with a setting (the width slider, the theme pair)
// are filtered as they appear.
onUpdated(applyFilter)

watch(
  () => props.open,
  async (open) => {
    if (!open) return
    filter.value = ''
    await nextTick()
    applyFilter()
    // Focus moves into the dialog so Escape and Tab behave, and so a screen
    // reader announces it rather than leaving the caret in the document. To
    // the search box, so typing finds a setting straight away.
    ;(filterInput.value ?? panel.value)?.focus()
  }
)

const editor = computed(() => settings.value.editor)

/** Patches one editor field, keeping the rest of the group intact. */
async function patchEditor(patch: Partial<typeof settings.value.editor>): Promise<void> {
  await patchSettings({ editor: patch })
  invalidateCommands()
}

/** The text size showing now, which follows the theme until one is chosen. */
const fontSize = computed(() => {
  void themes.current
  return effectiveFontSize(settings.value.editor)
})

/**
 * Width is one of three kinds — the theme's, a pixel value, or the whole pane —
 * so the kind is chosen first and the slider appears only for pixels.
 */
const width = computed(() => validWidth(editor.value.contentWidth))
const widthKind = computed(() =>
  width.value === null ? 'theme' : width.value === 'full' ? 'full' : 'custom'
)
/** A custom width starts from the column as it is now, not from an arbitrary number. */
function currentColumnWidth(): number {
  const col = document.querySelector('.editor-host') as HTMLElement | null
  // The content box, which is what max-width limits; the padding is extra.
  // Less the gutters either side of the text: the width set is the text's.
  const px = col ? parseFloat(getComputedStyle(col).width) : NaN
  const gutter = parseFloat(
    getComputedStyle(document.documentElement).getPropertyValue('--doc-gutter')
  )
  return Number.isFinite(px) ? px - 2 * (Number.isFinite(gutter) ? gutter : 0) : 900
}
async function setWidthKind(kind: string): Promise<void> {
  if (kind === 'theme') await setContentWidth(null)
  else if (kind === 'full') await setContentWidth('full')
  else await setContentWidth(currentColumnWidth())
}

/** Fonts most Windows machines have, offered as the field is typed in; any installed name works. */
const FONT_FIELDS = [
  {
    key: 'fontFamily',
    label: 'Text font',
    choices: [
      'Segoe UI',
      'Calibri',
      'Cambria',
      'Candara',
      'Constantia',
      'Georgia',
      'Palatino Linotype',
      'Sitka Text',
      'Times New Roman',
      'Verdana',
    ],
  },
  {
    key: 'codeFontFamily',
    label: 'Code font',
    choices: ['Cascadia Code', 'Cascadia Mono', 'Consolas', 'Courier New', 'Lucida Console'],
  },
] as const

/**
 * A font is stored by name alone. A value that is not one is put back to what
 * is stored, as the assets folder is: a field showing a font not in force
 * would mislead.
 */
async function setFontFamily(
  key: 'fontFamily' | 'codeFontFamily',
  el: HTMLInputElement
): Promise<void> {
  if (!el.value.trim()) {
    await patchEditor({ [key]: null })
    return
  }
  const name = validFontFamily(el.value)
  el.value = name ?? settings.value.editor[key] ?? ''
  if (name) await patchEditor({ [key]: name })
}

async function setLineHeight(el: HTMLInputElement): Promise<void> {
  const value = el.value.trim() === '' ? null : validLineHeight(Number(el.value))
  el.value = value === null ? '' : String(value)
  await patchEditor({ lineHeight: value })
}

function clearRecent(): Promise<void> {
  return window.api.app.clearRecent()
}

async function setAutoSaveDelay(el: HTMLInputElement): Promise<void> {
  const ms = autoSaveDelay(Number(el.value) * 1000)
  el.value = String(ms / 1000)
  await patchSettings({ autoSaveDelayMs: ms })
}

function setTheme(id: string): Promise<void> {
  return chooseTheme(id)
}

/**
 * Applied to the live editor as well as stored.
 *
 * These three have a runtime counterpart that is read per keystroke, so
 * storing alone would leave the setting and the behaviour disagreeing until
 * the next launch.
 */
async function setQuotes(value: boolean): Promise<void> {
  setPunctuation({ quotes: value })
  await patchEditor({ smartQuotes: value })
}
async function setDashes(value: boolean): Promise<void> {
  setPunctuation({ dashes: value })
  await patchEditor({ smartDashes: value })
}
async function setEllipses(value: boolean): Promise<void> {
  setPunctuation({ ellipses: value })
  await patchEditor({ smartEllipses: value })
}

async function setWhitespace(value: boolean): Promise<void> {
  setShowWhitespace(value)
  refreshDecorations()
  await patchEditor({ showWhitespace: value })
}

async function setTypewriter(value: boolean): Promise<void> {
  setEditorModes({ typewriter: value })
  await patchEditor({ typewriter: value })
}
async function setFocusMode(value: boolean): Promise<void> {
  setEditorModes({ focus: value })
  refreshDecorations()
  await patchEditor({ focusMode: value })
}

/** The languages spelling can be checked in, by name, read when the dialog opens. */
const spellingLanguages = ref<Array<{ code: string; name: string }>>([])
watch(
  () => props.open,
  async (open) => {
    if (!open || spellingLanguages.value.length) return
    // Made here, not at setup: the dialog is mounted, closed, at startup.
    const languageNames = new Intl.DisplayNames(undefined, { type: 'language' })
    const { available } = await window.api.spelling.languages()
    const named = (code: string): string => {
      try {
        return languageNames.of(code) ?? code
      } catch {
        return code
      }
    }
    spellingLanguages.value = available
      .map((code) => ({ code, name: named(code) }))
      .sort((a, b) => a.name.localeCompare(b.name))
  },
  { immediate: true }
)

async function setSpellingLanguage(code: string): Promise<void> {
  // Main checks the language and stores it; the change comes back as settings.
  await window.api.spelling.setLanguage(code || null)
}

async function setSpellcheck(value: boolean): Promise<void> {
  window.api.window.setSpellcheck(value)
  await patchEditor({ spellcheck: value })
}

/**
 * Clamped on the way in.
 *
 * A number field accepts anything typed into it, and a live-editor cap of zero
 * or a threshold of minus one would break the feature it configures rather
 * than merely configuring it oddly.
 */
function clamp(value: number, min: number, max: number, fallback: number): number {
  if (!Number.isFinite(value)) return fallback
  return Math.min(max, Math.max(min, Math.round(value)))
}

async function setLiveEditors(raw: number): Promise<void> {
  await patchEditor({ liveEditors: clamp(raw, 1, 30, 5) })
}

async function setOfferLines(raw: number): Promise<void> {
  const offer = clamp(raw, 100, 1_000_000, 5000)
  // Forcing source mode below the line where it is merely offered would mean
  // never offering it at all.
  const force = Math.max(offer, settings.value.editor.sourceModeForceLines)
  await patchEditor({ sourceModeOfferLines: offer, sourceModeForceLines: force })
}

async function setForceLines(raw: number): Promise<void> {
  const force = clamp(raw, 100, 1_000_000, 10000)
  const offer = Math.min(force, settings.value.editor.sourceModeOfferLines)
  await patchEditor({ sourceModeForceLines: force, sourceModeOfferLines: offer })
}

/**
 * The assets folder is free text, so it is validated before it is stored.
 *
 * A rejected value is put back to what is stored rather than silently ignored:
 * leaving the typed text in the field shows the user a setting that is not in
 * force anywhere.
 */
async function setAssetsFolder(el: HTMLInputElement): Promise<void> {
  const cleaned = el.value.trim().replace(/^[\\/]+|[\\/]+$/g, '')
  // An empty or escaping value would write pasted images somewhere other than
  // beside the document, which is the one thing this setting must not do.
  if (!cleaned || /^[a-zA-Z]:/.test(cleaned) || cleaned.includes('..')) {
    el.value = settings.value.editor.assetsFolder
    return
  }
  el.value = cleaned
  await patchEditor({ assetsFolder: cleaned })
}

function onKeydown(e: KeyboardEvent): void {
  if (e.key !== 'Escape' || !props.open) return
  e.preventDefault()
  e.stopPropagation()
  emit('close')
}

/**
 * Listened for on the window rather than only on the panel.
 *
 * Focus leaves the panel as soon as a field is blurred, and a modal that stops
 * responding to Escape because the caret moved is a trap.
 */
watch(
  () => props.open,
  (open) => {
    if (open) window.addEventListener('keydown', onKeydown, true)
    else window.removeEventListener('keydown', onKeydown, true)
  }
)

onBeforeUnmount(() => window.removeEventListener('keydown', onKeydown, true))
</script>

<template>
  <div v-if="open" class="prefs" @pointerdown.self="emit('close')">
    <div
      ref="panel"
      class="prefs__panel"
      role="dialog"
      aria-label="Preferences"
      aria-modal="true"
      tabindex="-1"
      @keydown="onKeydown"
    >
      <header class="prefs__head">
        <h2>Preferences</h2>
        <button class="prefs__close" aria-label="Close" @click="emit('close')">
          <IconClose />
        </button>
      </header>

      <div class="prefs__find">
        <input
          ref="filterInput"
          v-model="filter"
          class="prefs__filter"
          type="search"
          placeholder="Find a setting"
          aria-label="Find a setting"
          aria-controls="prefs-body"
        />
      </div>
      <p v-if="filter.trim() && matches === 0" class="prefs__none" role="status">
        No setting mentions “{{ filter.trim() }}”.
      </p>

      <div id="prefs-body" ref="body" class="prefs__body">
        <section class="prefs__section">
          <h3>Appearance</h3>

          <label v-if="!settings.followSystem.enabled" class="row">
            <span class="row__label">Theme</span>
            <select
              class="row__control"
              :value="themes.current"
              @change="setTheme(($event.target as HTMLSelectElement).value)"
            >
              <option v-for="t in themes.available" :key="t.id" :value="t.id">
                {{ t.name }}
              </option>
            </select>
          </label>
          <template v-else>
            <label v-for="mode in ['light', 'dark'] as const" :key="mode" class="row">
              <span class="row__label">{{ mode === 'light' ? 'Light theme' : 'Dark theme' }}</span>
              <select
                class="row__control"
                :value="settings.followSystem[mode]"
                @change="
                  patchSettings({
                    followSystem: { [mode]: ($event.target as HTMLSelectElement).value },
                  })
                "
              >
                <option v-for="t in themes.available" :key="t.id" :value="t.id">
                  {{ t.name }}
                </option>
              </select>
            </label>
          </template>

          <label class="row row--check">
            <input
              type="checkbox"
              :checked="settings.followSystem.enabled"
              @change="
                patchSettings({
                  followSystem: { enabled: ($event.target as HTMLInputElement).checked },
                })
              "
            />
            <span>Match the system’s light or dark mode</span>
          </label>

          <div class="row">
            <span class="row__label">Text size</span>
            <input
              class="row__control row__control--num"
              type="number"
              :min="FONT_MIN"
              :max="FONT_MAX"
              :value="fontSize"
              aria-label="Text size in pixels"
              @change="setFontSize(Number(($event.target as HTMLInputElement).value) || null)"
            />
            <span class="row__unit">px</span>
            <button
              class="row__reset"
              :disabled="editor.fontSize === null"
              title="Use the theme’s text size"
              @click="setFontSize(null)"
            >
              Reset
            </button>
          </div>

          <label class="row">
            <span class="row__label">Content width</span>
            <select
              class="row__control"
              :value="widthKind"
              aria-label="Content width"
              @change="setWidthKind(($event.target as HTMLSelectElement).value)"
            >
              <option value="theme">Theme default</option>
              <option value="custom">Custom</option>
              <option value="full">Full width</option>
            </select>
          </label>

          <label v-if="typeof width === 'number'" class="row">
            <input
              class="row__slider"
              type="range"
              :min="WIDTH_MIN"
              :max="WIDTH_MAX"
              step="20"
              :value="width"
              aria-label="Content width in pixels"
              @input="setContentWidth(Number(($event.target as HTMLInputElement).value))"
            />
            <span class="row__value">{{ width }} px</span>
          </label>
          <p class="hint">
            View ▸ Zoom and Ctrl+wheel change the text size too. Exports keep the theme’s layout.
          </p>

          <div v-for="f in FONT_FIELDS" :key="f.key" class="row">
            <span class="row__label">{{ f.label }}</span>
            <input
              class="row__control"
              type="text"
              :list="`${f.key}-choices`"
              :value="editor[f.key] ?? ''"
              placeholder="Theme default"
              :aria-label="f.label"
              spellcheck="false"
              @change="setFontFamily(f.key, $event.target as HTMLInputElement)"
            />
            <datalist :id="`${f.key}-choices`">
              <option v-for="name in f.choices" :key="name" :value="name" />
            </datalist>
            <button
              class="row__reset"
              :disabled="editor[f.key] === null"
              :title="`Use the theme’s ${f.label.toLowerCase()}`"
              @click="patchEditor({ [f.key]: null })"
            >
              Reset
            </button>
          </div>

          <div class="row">
            <span class="row__label">Line height</span>
            <input
              class="row__control row__control--num"
              type="number"
              :min="LINE_HEIGHT_MIN"
              :max="LINE_HEIGHT_MAX"
              step="0.05"
              :value="editor.lineHeight ?? ''"
              placeholder="Theme"
              aria-label="Line height"
              @change="setLineHeight($event.target as HTMLInputElement)"
            />
            <button
              class="row__reset"
              :disabled="editor.lineHeight === null"
              title="Use the theme’s line height"
              @click="patchEditor({ lineHeight: null })"
            >
              Reset
            </button>
          </div>

          <label class="row row--check">
            <input
              type="checkbox"
              :checked="settings.statusBar"
              @change="patchSettings({ statusBar: ($event.target as HTMLInputElement).checked })"
            />
            <span>Show status bar</span>
          </label>

          <label class="row row--check">
            <input
              type="checkbox"
              :checked="settings.toolbar"
              @change="patchSettings({ toolbar: ($event.target as HTMLInputElement).checked })"
            />
            <span>Show toolbar</span>
          </label>
        </section>

        <section class="prefs__section">
          <h3>Files</h3>

          <label class="row row--check">
            <input
              type="checkbox"
              :checked="settings.session.restore"
              @change="
                patchSettings({
                  session: { restore: ($event.target as HTMLInputElement).checked },
                })
              "
            />
            <span>Reopen documents from last time</span>
          </label>

          <label class="row row--check">
            <input
              type="checkbox"
              :checked="settings.autoSave"
              @change="patchSettings({ autoSave: ($event.target as HTMLInputElement).checked })"
            />
            <span>Save automatically</span>
          </label>

          <label class="row">
            <span class="row__label">After a pause in typing of</span>
            <input
              class="row__control row__control--num"
              type="number"
              :min="AUTOSAVE_MIN_MS / 1000"
              :max="AUTOSAVE_MAX_MS / 1000"
              step="0.1"
              :value="autoSaveDelay(settings.autoSaveDelayMs) / 1000"
              :disabled="!settings.autoSave"
              aria-label="Pause before saving, in seconds"
              @change="setAutoSaveDelay($event.target as HTMLInputElement)"
            />
            <span class="row__unit">s</span>
          </label>
          <p class="hint">
            Files are saved a moment after you stop typing, and when you switch away. New documents
            still ask for a name.
          </p>

          <label class="row">
            <span class="row__label">Line endings for new documents</span>
            <select
              class="row__control"
              :value="settings.newFileEol"
              @change="
                patchSettings({
                  newFileEol: ($event.target as HTMLSelectElement).value === 'lf' ? 'lf' : 'crlf',
                })
              "
            >
              <option value="crlf">Windows (CRLF)</option>
              <option value="lf">Unix (LF)</option>
            </select>
          </label>
          <p class="hint">Files you open keep their own.</p>

          <div class="row">
            <span class="row__label">
              Recent files: {{ settings.recentFiles.length || 'none' }}
            </span>
            <button
              class="row__reset"
              :disabled="settings.recentFiles.length === 0"
              title="Forget them, in Open Recent and on the welcome screen"
              @click="clearRecent"
            >
              Clear
            </button>
          </div>
        </section>

        <section class="prefs__section">
          <h3>Editing</h3>

          <label class="row row--check">
            <input
              type="checkbox"
              :checked="editor.spellcheck"
              @change="setSpellcheck(($event.target as HTMLInputElement).checked)"
            />
            <span>Check spelling</span>
          </label>

          <label class="row">
            <span class="row__label">Spelling language</span>
            <select
              class="row__control"
              :value="editor.spellcheckLanguage ?? ''"
              :disabled="!editor.spellcheck"
              @change="setSpellingLanguage(($event.target as HTMLSelectElement).value)"
            >
              <option value="">System default</option>
              <option v-for="l in spellingLanguages" :key="l.code" :value="l.code">
                {{ l.name }}
              </option>
            </select>
          </label>

          <label class="row row--check">
            <input
              type="checkbox"
              :checked="editor.showWhitespace"
              @change="setWhitespace(($event.target as HTMLInputElement).checked)"
            />
            <span>Show whitespace</span>
          </label>

          <label class="row row--check">
            <input
              type="checkbox"
              :checked="editor.focusMode"
              @change="setFocusMode(($event.target as HTMLInputElement).checked)"
            />
            <span>Focus mode</span>
          </label>

          <label class="row row--check">
            <input
              type="checkbox"
              :checked="editor.typewriter"
              @change="setTypewriter(($event.target as HTMLInputElement).checked)"
            />
            <span>Typewriter scrolling</span>
          </label>
        </section>

        <section class="prefs__section">
          <h3>Smart punctuation</h3>

          <label class="row row--check">
            <input
              type="checkbox"
              :checked="editor.smartQuotes"
              @change="setQuotes(($event.target as HTMLInputElement).checked)"
            />
            <span>Curly quotes</span>
          </label>

          <label class="row row--check">
            <input
              type="checkbox"
              :checked="editor.smartDashes"
              @change="setDashes(($event.target as HTMLInputElement).checked)"
            />
            <span>En and em dashes</span>
          </label>

          <label class="row row--check">
            <input
              type="checkbox"
              :checked="editor.smartEllipses"
              @change="setEllipses(($event.target as HTMLInputElement).checked)"
            />
            <span>Ellipses</span>
          </label>
        </section>

        <section class="prefs__section">
          <h3>Images</h3>

          <label class="row">
            <span class="row__label">Assets folder</span>
            <input
              class="row__control"
              type="text"
              :value="editor.assetsFolder"
              @change="setAssetsFolder($event.target as HTMLInputElement)"
            />
          </label>
          <p class="hint">
            Pasted images are written here, beside the document, and linked by relative path.
          </p>
        </section>

        <section class="prefs__section">
          <h3>Large documents</h3>

          <label class="row">
            <span class="row__label">Offer source mode above</span>
            <input
              class="row__control row__control--num"
              type="number"
              min="100"
              step="500"
              :value="editor.sourceModeOfferLines"
              @change="setOfferLines(Number(($event.target as HTMLInputElement).value))"
            />
          </label>

          <label class="row">
            <span class="row__label">Default to source mode above</span>
            <input
              class="row__control row__control--num"
              type="number"
              min="100"
              step="500"
              :value="editor.sourceModeForceLines"
              @change="setForceLines(Number(($event.target as HTMLInputElement).value))"
            />
          </label>

          <label class="row">
            <span class="row__label">Editors kept in memory</span>
            <input
              class="row__control row__control--num"
              type="number"
              min="1"
              max="30"
              :value="editor.liveEditors"
              @change="setLiveEditors(Number(($event.target as HTMLInputElement).value))"
            />
          </label>
          <p class="hint">
            Each one keeps a tab's undo history alive across switches, and costs memory.
          </p>
        </section>
      </div>
    </div>
  </div>
</template>

<style scoped>
.prefs {
  position: fixed;
  inset: 0;
  z-index: 200;
  display: flex;
  justify-content: center;
  padding-top: 8vh;
  background: rgba(0, 0, 0, 0.35);
}
.prefs__panel {
  width: min(560px, 92vw);
  max-height: 78vh;
  display: flex;
  flex-direction: column;
  background: var(--menu-bg);
  color: var(--menu-fg);
  border: 1px solid var(--menu-border);
  border-radius: 8px;
  box-shadow: var(--menu-shadow);
  overflow: hidden;
  outline: none;
}
.prefs__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid var(--menu-border);
}
.prefs__head h2 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
}
.prefs__close {
  display: grid;
  place-items: center;
  width: 24px;
  height: 24px;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: inherit;
  cursor: default;
}
.prefs__close:hover {
  background: var(--menu-hover);
}
.prefs__body {
  overflow: auto;
  padding: 4px 16px 16px;
}
/* Rows are flex boxes, which would override the attribute's own display: none. */
.prefs__body [hidden] {
  display: none;
}
.prefs__find {
  padding: 10px 16px 0;
}
.prefs__filter {
  width: 100%;
  box-sizing: border-box;
  padding: 5px 8px;
  background: var(--menu-hover);
  color: inherit;
  border: 1px solid var(--menu-border);
  border-radius: 4px;
  font: inherit;
  font-size: 13px;
}
.prefs__none {
  margin: 0;
  padding: 16px;
  font-size: 13px;
  color: var(--menu-fg-muted);
}
.prefs__section {
  padding: 12px 0;
  border-bottom: 1px solid var(--menu-border);
}
.prefs__section:last-child {
  border-bottom: 0;
}
.prefs__section h3 {
  margin: 0 0 10px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  opacity: 0.6;
}
.row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 5px 0;
  font-size: 13px;
}
.row--check {
  justify-content: flex-start;
  gap: 8px;
}
.row__label {
  flex: 1;
}
.row__control {
  min-width: 170px;
  padding: 4px 6px;
  background: var(--menu-hover);
  color: inherit;
  border: 1px solid var(--menu-border);
  border-radius: 4px;
  font: inherit;
  font-size: 12px;
}
.row__control--num {
  min-width: 90px;
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.row__unit {
  margin-left: -6px;
  opacity: 0.7;
}
.row__reset {
  padding: 3px 8px;
  background: transparent;
  color: inherit;
  border: 1px solid var(--menu-border);
  border-radius: 4px;
  font: inherit;
  font-size: 12px;
  cursor: default;
}
.row__reset:disabled {
  opacity: 0.4;
}
.row__slider {
  flex: 1;
  accent-color: var(--chrome-accent);
}
.row__value {
  min-width: 64px;
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.hint {
  margin: 4px 0 0;
  font-size: 11px;
  opacity: 0.55;
  line-height: 1.5;
}
</style>
